import type { Organization, OrganizationSection } from "./organization.types";

export interface OrganizationCapabilities {
  products: boolean;
  services: boolean;
  tours: boolean;
  gallery: boolean;
  contact: boolean;
  location: boolean;
  map: boolean;
}

export const hasProducts = (organization: Organization): boolean =>
  Boolean(organization.productCatalog?.products.length);

export const hasServices = (organization: Organization): boolean =>
  Boolean(organization.serviceCatalog?.services.length);

export const hasTours = (organization: Organization): boolean =>
  Boolean(organization.tourism?.tours.length);

export const hasGallery = (organization: Organization): boolean =>
  Boolean(organization.media?.images?.length || organization.media?.videos?.length);

export const hasContact = (organization: Organization): boolean => {
  const contact = organization.contact;
  if (!contact) return false;

  return Boolean(
    contact.emails?.length ||
      contact.phones?.length ||
      contact.address?.es ||
      (contact.social && Object.values(contact.social).some(Boolean))
  );
};

export const hasLocation = (organization: Organization): boolean => {
  const location = organization.location;
  if (!location) return false;

  return Boolean(
    location.name?.es ||
      location.address?.es ||
      location.coordinates ||
      location.directions?.es ||
      location.hours?.length
  );
};

export const hasMap = (organization: Organization): boolean =>
  Boolean(organization.location?.coordinates);

export const getOrganizationCapabilities = (
  organization: Organization
): OrganizationCapabilities => ({
  products: hasProducts(organization),
  services: hasServices(organization),
  tours: hasTours(organization),
  gallery: hasGallery(organization),
  contact: hasContact(organization),
  location: hasLocation(organization),
  map: hasMap(organization),
});

export const isOrganizationSectionAvailable = (
  organization: Organization,
  section: OrganizationSection
): boolean => {
  switch (section.type) {
    case "about":
      return Boolean(organization.identity.description?.es || organization.identity.summary?.es);
    case "institutional":
      return Boolean(
        organization.institutional?.history?.es ||
          organization.institutional?.mission?.es ||
          organization.institutional?.vision?.es ||
          organization.institutional?.objectives?.length
      );
    case "products":
      return hasProducts(organization);
    case "services":
      return hasServices(organization);
    case "tours":
      return hasTours(organization);
    case "gallery":
      return hasGallery(organization);
    case "contact":
      return hasContact(organization);
    case "map":
      return hasMap(organization);
  }
};

export const getVisibleOrganizationSections = (
  organization: Organization
): OrganizationSection[] =>
  organization.sections.filter((section) =>
    isOrganizationSectionAvailable(organization, section)
  );
